import React, { ChangeEvent, useContext, useEffect, useState } from 'react';

import getTimeStamp from 'getTimeStamp';
import getIdentifier from 'getIdentifier';
import CurrentUserContext from 'contexts/CurrentUserContext';
import usePerformanceSlideOutFilterInput from 'components/PerformanceBoards/hooks/usePerformanceSlideOutFilterInput';
import useSlideoutDateScope from 'components/PerformanceBoards/hooks/useSlideoutDateScope';
import useSaveWidget from 'components/PerformanceBoards/hooks/useSaveWidget';
import MetricSlideoutWizard from './MetricSlideoutWizard';

const MetricSlideoutWizardContainer = ({
  close,
  editingSection,
}: {
  close: () => void;
  editingSection: PerformanceBoardTypes.SlideOut.MetricsSection | undefined;
}) => {
  const { id: currentUserId } = useContext(CurrentUserContext);

  const [id] = useState<string>(
    editingSection ? editingSection.id : getIdentifier(),
  );
  const [createdBy] = useState<string>(
    editingSection ? editingSection.createdBy : currentUserId,
  );
  const [createdOn] = useState<string>(
    editingSection ? editingSection.createdOn : getTimeStamp(),
  );
  const [title, setTitle] = useState<string>(
    editingSection ? editingSection.title : '',
  );
  const [metricIds, setMetricIds] = useState<string[]>(
    editingSection ? editingSection.metricIds : [],
  );
  const [widgetDateRange, setWidgetDateRange] =
    useState<PerformanceBoardTypes.SlideOut.SlideOutDateRange>(
      editingSection
        ? editingSection.dateRange
        : {
            mode: 'Current Interval',
            isToDate: false,
          },
    );
  const [metricSection, setMetricSection] = useState<
    PerformanceBoardTypes.SlideOut.MetricsSection | undefined
  >();

  const filterInput = usePerformanceSlideOutFilterInput({});
  const dateScope = useSlideoutDateScope(widgetDateRange);
  const saveWidget = useSaveWidget({
    section: metricSection,
    editingSection,
  });

  const isDisabled = metricIds.length === 0 || title === '';

  const onTitleChanged = (event: ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();
    setTitle(event.target.value);
  };

  useEffect(() => {
    if (isDisabled) {
      setMetricSection(undefined);
      return;
    }

    const newMetricSection = {
      id: id,
      createdBy: createdBy,
      updatedBy: currentUserId,
      createdOn: createdOn,
      updatedOn: getTimeStamp(),
      sectionType: 'Metric Widget' as 'Metric Widget',
      title: title,
      metricIds: metricIds,
      dateRange: widgetDateRange,
    };

    setMetricSection(newMetricSection);
  }, [
    createdBy,
    createdOn,
    currentUserId,
    id,
    isDisabled,
    metricIds,
    title,
    widgetDateRange,
  ]);

  return (
    <MetricSlideoutWizard
      saveWidget={saveWidget}
      title={title}
      onTitleChanged={onTitleChanged}
      widgetDateRange={widgetDateRange}
      setWidgetDateRange={setWidgetDateRange}
      isDisabled={isDisabled}
      filterInput={filterInput}
      dateScope={dateScope}
      metricIds={metricIds}
      setMetricIds={setMetricIds}
      metricSection={metricSection}
      close={close}
    />
  );
};

export default MetricSlideoutWizardContainer;
